import type { AuthUser } from '@/shared/lib/auth/auth-provider'
import { canAccess } from '@/shared/lib/permissions/can-access'
import {
  isInfraTenant,
  canSeeRealReconciliation,
  canSeeDatalakeDashboards,
  isEcobankTenant,
} from '@/shared/lib/auth/tenant-constants'
import type { NavGroup, NavItem, NavSection } from './nav-config'

interface NavVisibilityOptions {
  user: AuthUser | null
  /** Hide inactive items — used when the "Active only" filter is on. */
  activeOnly?: boolean
  /**
   * Dev bypass: when the user's permissions map is empty, show permission-gated
   * items anyway (useful during local dev before a real role is assigned). Does
   * NOT apply to `requireAdmin` items — those always honor the admin flag.
   */
  hasAnyPermissions?: boolean
}

export function isNavItemVisible(item: NavItem, options: NavVisibilityOptions): boolean {
  const { user, activeOnly = false, hasAnyPermissions = true } = options

  if (item.tenantOnly === 'infra' && !isInfraTenant(user)) return false
  // Reconciliation module gating: real screens for allowlisted tenants only;
  // mock/"upgrade" screens for everyone else.
  if (item.tenantOnly === 'recon-real' && !canSeeRealReconciliation(user)) return false
  if (item.tenantOnly === 'recon-mock' && canSeeRealReconciliation(user)) return false
  // Ecobank demo screens (Anexo 8) — only the Ecobank tenant.
  if (item.tenantOnly === 'ecobank' && !isEcobankTenant(user)) return false
  // Datalake dashboards POC (UC-100) — only the allowlisted tenants.
  if (item.tenantOnly === 'datalake' && !canSeeDatalakeDashboards(user)) return false
  if (activeOnly && item.status === 'inactive') return false
  if (item.status === 'inactive') return true
  if (item.visibleFor) return item.visibleFor(user)
  if (item.requireAdmin) {
    return (user?.isSuperAdmin ?? false) || (user?.isAdmin ?? false)
  }
  if (!item.permission) return true
  if (!hasAnyPermissions) return true
  return canAccess(user, item.permission)
}

/**
 * The visible items of a mega-menu group, keeping its sub-headers. A sub-header
 * travels on the first item of its block, so when that item is hidden (no
 * permission, "Active only"...) the next visible item of the same block takes
 * it over. Without this, hiding the first item would silently drop the
 * sub-header and its block would read as part of the block above.
 */
export function visibleItemsWithSubheaders(items: NavItem[], isVisible: (item: NavItem) => boolean): NavItem[] {
  const visible: NavItem[] = []
  let pendingSubheaderKey: string | undefined
  for (const item of items) {
    if (item.subheaderKey) pendingSubheaderKey = item.subheaderKey
    if (!isVisible(item)) continue
    visible.push(pendingSubheaderKey && !item.subheaderKey ? { ...item, subheaderKey: pendingSubheaderKey } : item)
    pendingSubheaderKey = undefined
  }
  return visible
}

/**
 * The columns of a mega-menu section worth showing: those with at least one
 * visible item. A column whose items are all hidden for the user (e.g. the
 * Reconciliations "Reporting" column, real-module only, on a mock tenant) is
 * dropped instead of rendering as an empty header.
 */
export function visibleNavGroups(groups: NavGroup[], isVisible: (item: NavItem) => boolean): NavGroup[] {
  return groups.filter((group) => group.items.some(isVisible))
}

interface NavSectionVisibilityOptions {
  user: AuthUser | null
  /** Hide inactive sections — used when the "Active only" filter is on. */
  activeOnly?: boolean
}

/**
 * Visibility of a direct-link section (one with `path`, no items/groups).
 * `requireSuperAdmin` always honors the flag — there is no dev bypass, same as
 * `requireAdmin` items. `permission` goes through `canAccess` (no dev bypass either).
 */
export function isNavSectionVisible(section: NavSection, options: NavSectionVisibilityOptions): boolean {
  const { user, activeOnly = false } = options

  if (activeOnly && section.status === 'inactive') return false
  if (section.requireSuperAdmin) return user?.isSuperAdmin ?? false
  if (section.permission) return canAccess(user, section.permission)
  return true
}
